/**
 * prompt-labels (aka "tags") domain - built on apiClient via useRequest, not
 * useAppServices(). Shared by:
 *   - Accounts/components/CreateModal/TagsSelect.js, Accounts/components/
 *     Tabs/PromptLabels.js (assigning labels to an account)
 *   - UiPrompts/promptlabels/* (the admin CRUD screen for labels themselves)
 *
 * Query key uses "prompt-labels" (not "agent-categories") - the convention
 * already fixed app-wide for this same promptslabel.Get endpoint during the
 * earlier TanStack migration.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";

const { GET, POST, PUT, DELETE } = ReqMethods;

export const promptLabelKeys = {
  all: ["prompt-labels"],
  list: (agencyId, type) => ["prompt-labels", type, agencyId],
};

export function usePromptLabels({ agencyId, type = "agency" }, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: promptLabelKeys.list(agencyId, type),
    queryFn: async () => {
      const { response } = await request(GET, "/promptslabel", {
        query: `type=${type}&agency_id=${agencyId}`,
      });
      return response?.data || [];
    },
    enabled: !!agencyId,
    ...options,
  });
}

export function useCreatePromptLabel() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/promptslabel/create", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: promptLabelKeys.all }),
  });
}

export function useUpdatePromptLabel() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/promptslabel", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: promptLabelKeys.all }),
  });
}

export function useDeletePromptLabel() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (query) => request(DELETE, "/promptslabel", { query }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: promptLabelKeys.all }),
  });
}

// Same DELETE /promptslabel endpoint as useDeletePromptLabel above, but
// UiPrompts/promptlabels/CustomPrompt.js's delete call passes toaster: true
// (every other existing call site doesn't) - added separately rather than
// changing useDeletePromptLabel's signature, since that hook already has
// other callers depending on its current shape.
export function useDeletePromptLabelWithToaster() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (query) => request(DELETE, "/promptslabel", { query, toaster: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: promptLabelKeys.all }),
  });
}

export function usePromptLabelSelectValuesById(query, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: [...promptLabelKeys.all, "select-values", query],
    queryFn: async () => {
      const { response } = await request(GET, "/promptslabel/GetselectvaluesbyID", { query });
      return response?.data || [];
    },
    enabled: !!query,
    ...options,
  });
}

export function useAddFavouritePromptLabel() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/promptslabel/add-favourite", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: promptLabelKeys.all }),
  });
}
